import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh } from '../context/DataRefreshContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Calendar,
  FileText,
  DollarSign,
  TrendingUp,
  Edit,
  Save,
  X,
  ChevronRight,
  ShoppingBag,
  Ruler,
  Star,
  Clock,
  CheckCircle,
  Lock,
  ExternalLink,
  Send,
  AlertTriangle,
  Wallet,
  Gift,
  Users,
  History,
  Eye,
  XCircle,
  MoreVertical,
  Trash2
} from 'lucide-react';
import EliminarVentaModal from '../components/EliminarVentaModal';
import BienvenidaMiJPM from '../components/mijpm/BienvenidaMiJPM';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_DOCUMENTO = [
  { value: 'CC', label: 'Cédula de Ciudadanía' },
  { value: 'CE', label: 'Cédula de Extranjería' },
  { value: 'NIT', label: 'NIT' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'TI', label: 'Tarjeta de Identidad' },
  { value: 'OTRO', label: 'Otro' }
];

const formatPrice = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ESTADO_COLORS = {
  borrador: 'bg-zinc-500/20 text-zinc-300',
  enviada: 'bg-blue-500/20 text-blue-300',
  aprobada: 'bg-green-500/20 text-green-300',
  rechazada: 'bg-red-500/20 text-red-300',
  vencida: 'bg-amber-500/20 text-amber-300',
  pendiente: 'bg-yellow-500/20 text-yellow-300',
  en_proceso: 'bg-purple-500/20 text-purple-300',
  completado: 'bg-emerald-500/20 text-emerald-300',
  al_dia: 'bg-emerald-500/20 text-emerald-300',
  mora: 'bg-red-500/20 text-red-300',
  pagado: 'bg-green-500/20 text-green-300',
  anulada: 'bg-red-500/20 text-red-300'
};

// Componente de Pestañas
const TabButton = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all text-sm font-medium whitespace-nowrap ${
      active 
        ? 'bg-[#D4AF37] text-black' 
        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="hidden sm:inline">{label}</span>
    {badge !== undefined && badge > 0 && (
      <span className={`px-1.5 py-0.5 rounded-full text-xs ${active ? 'bg-black/20 text-black' : 'bg-zinc-700 text-zinc-300'}`}>
        {badge}
      </span>
    )}
  </button>
);

const ClienteDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { refreshTimestamps } = useDataRefresh();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [puntos, setPuntos] = useState({});
  const [referidos, setReferidos] = useState({});
  const [estadisticas, setEstadisticas] = useState({});
  const [registrosSinVincular, setRegistrosSinVincular] = useState(null);
  
  // Estado de UI
  const [activeTab, setActiveTab] = useState('resumen');
  const [editando, setEditando] = useState(false);
  const [datosEdicion, setDatosEdicion] = useState({});
  const [guardando, setGuardando] = useState(false);
  
  // Mi JPM
  const [miJpmStatus, setMiJpmStatus] = useState(null);
  const [cargandoMiJpm, setCargandoMiJpm] = useState(false);
  
  // Eliminar venta
  const [ventaAEliminar, setVentaAEliminar] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(null); // ID de la venta con menú abierto

  const fetchCliente = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clientes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCliente(response.data.cliente);
      setFichas(response.data.fichas || []);
      setCotizaciones(response.data.cotizaciones || []);
      setVentas(response.data.ventas || []);
      setPagos(response.data.pagos || []);
      setPuntos(response.data.puntos || {});
      setReferidos(response.data.referidos || {});
      setEstadisticas(response.data.estadisticas || {});
      setRegistrosSinVincular(response.data.registros_sin_vincular);
      setDatosEdicion(response.data.cliente);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al cargar cliente';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCliente();
  }, [fetchCliente]);

  // Refrescar cuando otra vista invalide datos de este cliente
  useEffect(() => {
    if (id) {
      fetchCliente();
    }
  }, [refreshTimestamps.clientes, refreshTimestamps.ventas, refreshTimestamps.cotizaciones, refreshTimestamps.puntos]);

  // Cargar status de Mi JPM
  const fetchMiJpmStatus = useCallback(async () => {
    if (!id) return;
    setCargandoMiJpm(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mi-jpm/cliente/${id}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMiJpmStatus(response.data);
    } catch (err) {
      console.error('Error al cargar Mi JPM status:', err);
    } finally {
      setCargandoMiJpm(false);
    }
  }, [id]);

  useEffect(() => {
    if (cliente) {
      fetchMiJpmStatus();
    }
  }, [cliente, fetchMiJpmStatus]);

  // Habilitar Mi JPM manualmente
  const habilitarMiJpm = async () => {
    if (!window.confirm(`¿Crear el acceso a Mi JPM para ${cliente?.nombre || 'este cliente'}?\nUsuario y contraseña inicial: ${miJpmStatus?.correo || cliente?.correo}`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/mi-jpm/cliente/${id}/habilitar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mi JPM habilitado. El mensaje de bienvenida quedó listo para enviar.');
      fetchMiJpmStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al habilitar Mi JPM');
    }
  };

  const guardarCambios = async () => {
    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/clientes/${id}`, datosEdicion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cliente actualizado');
      setEditando(false);
      fetchCliente();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setGuardando(false);
    }
  };

  // Estados de carga y error
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
          <span className="text-zinc-400 text-sm">Cargando expediente...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg mb-2">Error al cargar el expediente</p>
          <p className="text-zinc-400 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate('/clientes')}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
          >
            Volver a Clientes
          </button>
        </div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <p>Cliente no encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/clientes')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-serif">{cliente.nombre}</h1>
                  <span className="text-xs px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded">
                    {cliente.id_cliente}
                  </span>
                  {estadisticas.es_recurrente && (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">
                      <Star className="w-3 h-3" /> Recurrente
                    </span>
                  )}
                  {estadisticas.tiene_cartera_vencida && (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">
                      <AlertTriangle className="w-3 h-3" /> Cartera vencida
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-400">
                  {cliente.tipo_documento} {cliente.numero_documento}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {editando ? (
                <>
                  <button
                    onClick={() => {
                      setEditando(false);
                      setDatosEdicion(cliente);
                    }}
                    className="p-2 hover:bg-zinc-800 rounded-lg"
                    title="Cancelar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <button
                    onClick={guardarCambios}
                    disabled={guardando}
                    data-testid="cliente-guardar-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg disabled:opacity-50"
                  >
                    {guardando ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditando(true)}
                  data-testid="cliente-editar-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
                >
                  <Edit className="w-4 h-4" />
                  <span className="hidden sm:inline">Editar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tarjetas de Resumen */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <ShoppingBag className="w-4 h-4" />
              Total Compras
            </div>
            <p className="text-xl font-bold text-emerald-400">{formatPrice(estadisticas.total_compras)}</p>
            <p className="text-xs text-zinc-500">{estadisticas.total_ventas} orden(es)</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Pagado
            </div>
            <p className="text-xl font-bold text-green-400">{formatPrice(estadisticas.total_pagado)}</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <Wallet className="w-4 h-4" />
              Cartera
            </div>
            <p className={`text-xl font-bold ${estadisticas.total_pendiente > 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
              {formatPrice(estadisticas.total_pendiente)}
            </p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
              <Star className="w-4 h-4" />
              Puntos JPM
            </div>
            <p className="text-xl font-bold text-purple-400">{(estadisticas.puntos_disponibles || 0).toLocaleString()}</p>
            <p className="text-xs text-zinc-500">= {formatPrice(estadisticas.valor_puntos)}</p>
          </div>
        </div>

        {/* Pestañas */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          <TabButton 
            active={activeTab === 'resumen'} 
            onClick={() => setActiveTab('resumen')} 
            icon={User} 
            label="Datos" 
          />
          <TabButton 
            active={activeTab === 'ventas'} 
            onClick={() => setActiveTab('ventas')} 
            icon={ShoppingBag} 
            label="Ventas" 
            badge={estadisticas.total_ventas}
          />
          <TabButton 
            active={activeTab === 'pagos'} 
            onClick={() => setActiveTab('pagos')} 
            icon={Wallet} 
            label="Cuotas" 
            badge={pagos.length}
          />
          <TabButton 
            active={activeTab === 'cotizaciones'} 
            onClick={() => setActiveTab('cotizaciones')} 
            icon={FileText} 
            label="Cotizaciones" 
            badge={estadisticas.total_cotizaciones}
          />
          <TabButton 
            active={activeTab === 'fichas'} 
            onClick={() => setActiveTab('fichas')} 
            icon={Ruler} 
            label="Fichas" 
            badge={estadisticas.total_fichas}
          />
          <TabButton 
            active={activeTab === 'puntos'} 
            onClick={() => setActiveTab('puntos')} 
            icon={Gift} 
            label="Puntos" 
          />
          <TabButton 
            active={activeTab === 'referidos'} 
            onClick={() => setActiveTab('referidos')} 
            icon={Users} 
            label="Referidos" 
            badge={estadisticas.referidos_count}
          />
        </div>

        {/* Contenido de pestañas */}
        <div className="space-y-6">
          {/* TAB: Datos del Cliente */}
          {activeTab === 'resumen' && (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Información de contacto */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                  Información de Contacto
                </h2>
                
                {editando ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Nombre</label>
                      <input
                        type="text"
                        value={datosEdicion.nombre || ''}
                        onChange={(e) => setDatosEdicion(prev => ({ ...prev, nombre: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Tipo Doc.</label>
                        <select
                          value={datosEdicion.tipo_documento || 'CC'}
                          onChange={(e) => setDatosEdicion(prev => ({ ...prev, tipo_documento: e.target.value }))}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                        >
                          {TIPOS_DOCUMENTO.map(t => (
                            <option key={t.value} value={t.value}>{t.value}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Número</label>
                        <input
                          type="text"
                          value={datosEdicion.numero_documento || ''}
                          onChange={(e) => setDatosEdicion(prev => ({ ...prev, numero_documento: e.target.value }))}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Teléfono</label>
                      <input
                        type="tel"
                        value={datosEdicion.telefono || ''}
                        data-testid="cliente-edit-telefono"
                        onChange={(e) => setDatosEdicion(prev => ({ ...prev, telefono: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Correo</label>
                      <input
                        type="email"
                        value={datosEdicion.correo || ''}
                        onChange={(e) => setDatosEdicion(prev => ({ ...prev, correo: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Dirección</label>
                      <input
                        type="text"
                        value={datosEdicion.direccion || ''}
                        onChange={(e) => setDatosEdicion(prev => ({ ...prev, direccion: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Ciudad</label>
                      <input
                        type="text"
                        value={datosEdicion.ciudad || ''}
                        onChange={(e) => setDatosEdicion(prev => ({ ...prev, ciudad: e.target.value }))}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Notas Internas</label>
                      <textarea
                        value={datosEdicion.notas_internas || ''}
                        onChange={(e) => setDatosEdicion(prev => ({ ...prev, notas_internas: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm resize-none"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cliente.telefono && (
                      <a
                        href={`tel:${cliente.telefono}`}
                        className="flex items-center gap-3 p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                      >
                        <Phone className="w-4 h-4 text-zinc-400" />
                        <span>{cliente.telefono}</span>
                      </a>
                    )}
                    {cliente.correo && (
                      <a
                        href={`mailto:${cliente.correo}`}
                        className="flex items-center gap-3 p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                      >
                        <Mail className="w-4 h-4 text-zinc-400" />
                        <span className="truncate">{cliente.correo}</span>
                      </a>
                    )}
                    {cliente.direccion && (
                      <div className="flex items-center gap-3 p-2">
                        <MapPin className="w-4 h-4 text-zinc-400" />
                        <span>{cliente.direccion}{cliente.ciudad ? `, ${cliente.ciudad}` : ''}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 p-2 text-zinc-400 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>Cliente desde {formatDate(cliente.created_at)}</span>
                    </div>
                    {cliente.notas_internas && (
                      <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <p className="text-xs text-amber-400 mb-1">Notas internas:</p>
                        <p className="text-sm text-zinc-300">{cliente.notas_internas}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Mi JPM - Área Privada */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-emerald-400" />
                  Mi JPM
                </h2>
                
                {cargandoMiJpm && !miJpmStatus ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#D4AF37]"></div>
                  </div>
                ) : miJpmStatus ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {miJpmStatus.habilitado ? (
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Habilitado
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-zinc-700 text-zinc-400 rounded-lg text-sm">
                          <Lock className="w-4 h-4" />
                          No habilitado
                        </span>
                      )}
                    </div>
                    
                    {!miJpmStatus.habilitado && miJpmStatus.condiciones && user?.role === 'gerencia' && (
                      <div className="space-y-2 text-sm">
                        <p className="text-zinc-400">Para crear el acceso se necesita:</p>
                        <div className="flex items-center gap-2">
                          {miJpmStatus.condiciones.tiene_correo_valido ? (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className={miJpmStatus.condiciones.tiene_correo_valido ? 'text-zinc-300' : 'text-zinc-500'}>
                            Correo electrónico válido (será su usuario)
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500">No requiere ventas ni pagos. Solo Gerencia puede crear y enviar accesos.</p>
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-2 pt-2">
                      {miJpmStatus.habilitado ? (
                        user?.role === 'gerencia' && <BienvenidaMiJPM clienteId={miJpmStatus.cliente_id || id} />
                      ) : user?.role === 'gerencia' && (
                        <button
                          onClick={habilitarMiJpm}
                          disabled={!miJpmStatus.puede_habilitarse}
                          title={miJpmStatus.razon_no_habilitado || undefined}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="detalle-crear-acceso-mijpm"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Crear acceso a Mi JPM
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm">No se pudo cargar el estado de Mi JPM</p>
                )}
              </div>
            </div>
          )}

          {/* TAB: Ventas */}
          {activeTab === 'ventas' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-emerald-400" />
                Órdenes de Venta
              </h2>
              
              {ventas.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No hay ventas registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ventas.map(venta => (
                    <div
                      key={venta.id}
                      className="relative flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors gap-3"
                    >
                      {/* Contenido clickeable para ir al detalle */}
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/ventas/${venta.id}`)}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#D4AF37]">{venta.numero}</p>
                          {venta.cotizacion_numero && (
                            <span className="text-xs text-zinc-500">
                              desde {venta.cotizacion_numero}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${ESTADO_COLORS[venta.estado] || ESTADO_COLORS.pendiente}`}>
                            {venta.estado}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400 mt-1">
                          {formatDate(venta.created_at)} • {venta.created_by_nombre}
                        </p>
                        {venta.puntos_canjeados > 0 && (
                          <p className="text-xs text-purple-400 mt-1">
                            🎁 {venta.puntos_canjeados} puntos canjeados ({formatPrice(venta.descuento_puntos)})
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div 
                          className="text-right cursor-pointer"
                          onClick={() => navigate(`/ventas/${venta.id}`)}
                        >
                          <p className="font-bold text-emerald-400">{formatPrice(venta.total_final)}</p>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-green-400">
                              Pagado: {formatPrice(venta.monto_pagado)}
                            </span>
                            {venta.monto_pendiente > 0 && (
                              <span className="text-amber-400">
                                Pend: {formatPrice(venta.monto_pendiente)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500 mt-0.5">
                            {venta.cuotas_pagadas}/{venta.cuotas_total} cuotas
                            {venta.cuotas_vencidas > 0 && (
                              <span className="text-red-400 ml-1">
                                ({venta.cuotas_vencidas} vencidas)
                              </span>
                            )}
                          </p>
                        </div>
                        
                        {/* Menú de acciones - Solo para gerencia */}
                        {user?.role === 'gerencia' && (
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAbierto(menuAbierto === venta.id ? null : venta.id);
                              }}
                              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                              data-testid={`venta-menu-${venta.numero}`}
                            >
                              <MoreVertical className="w-4 h-4 text-zinc-400" />
                            </button>
                            
                            {menuAbierto === venta.id && (
                              <>
                                {/* Overlay para cerrar el menú */}
                                <div 
                                  className="fixed inset-0 z-10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuAbierto(null);
                                  }}
                                />
                                {/* Menú dropdown */}
                                <div className="absolute right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20 py-1 min-w-[160px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMenuAbierto(null);
                                      setVentaAEliminar(venta);
                                    }}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                                    data-testid={`eliminar-venta-${venta.numero}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Eliminar venta
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Chevron solo cuando no es gerencia */}
                        {user?.role !== 'gerencia' && (
                          <ChevronRight 
                            className="w-5 h-5 text-zinc-500 cursor-pointer" 
                            onClick={() => navigate(`/ventas/${venta.id}`)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: Cuotas */}
          {activeTab === 'pagos' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-400" />
                Cuotas y Cartera
              </h2>
              
              {pagos.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No hay cuotas registradas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-zinc-400 border-b border-zinc-800">
                        <th className="text-left py-2 px-2">Orden</th>
                        <th className="text-left py-2 px-2">Cuota</th>
                        <th className="text-right py-2 px-2">Programado</th>
                        <th className="text-right py-2 px-2">Abonado</th>
                        <th className="text-center py-2 px-2">Fecha Límite</th>
                        <th className="text-center py-2 px-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagos.map((pago, idx) => {
                        // Determinar si la cuota está vencida
                        // Una cuota está vencida si:
                        // - Tiene fecha límite pasada Y
                        // - No está completamente pagada (estado != 'pagada')
                        const fechaLimite = pago.fecha_limite ? new Date(pago.fecha_limite) : null;
                        const estaVencida = fechaLimite && fechaLimite < new Date() && pago.estado !== 'pagada';
                        
                        return (
                          <tr 
                            key={idx} 
                            className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer ${estaVencida ? 'bg-red-900/10' : ''}`}
                            onClick={() => navigate(`/ventas/${pago.orden_id}`)}
                          >
                            <td className="py-3 px-2">
                              <span className="text-[#D4AF37]">{pago.orden_numero}</span>
                            </td>
                            <td className="py-3 px-2">
                              <span>Cuota {pago.cuota_numero}</span>
                              {pago.descripcion && (
                                <p className="text-xs text-zinc-500">{pago.descripcion}</p>
                              )}
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className="font-medium">{formatPrice(pago.valor_programado)}</span>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className={`font-medium ${pago.valor_abonado > 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                                {formatPrice(pago.valor_abonado || 0)}
                              </span>
                              {pago.saldo_cuota > 0 && pago.valor_abonado > 0 && (
                                <p className="text-xs text-amber-400">Saldo: {formatPrice(pago.saldo_cuota)}</p>
                              )}
                            </td>
                            <td className={`py-3 px-2 text-center ${estaVencida ? 'text-red-400 font-medium' : 'text-zinc-400'}`}>
                              {formatDate(pago.fecha_limite)}
                              {estaVencida && <p className="text-xs">Vencida</p>}
                            </td>
                            <td className="py-3 px-2 text-center">
                              {pago.estado === 'pagada' ? (
                                <span className="flex items-center justify-center gap-1 text-green-400 text-xs">
                                  <CheckCircle className="w-4 h-4" />
                                  Pagada
                                </span>
                              ) : pago.estado === 'parcial' && estaVencida ? (
                                <span className="flex flex-col items-center justify-center gap-0.5 text-xs">
                                  <span className="flex items-center gap-1 text-red-400">
                                    <AlertTriangle className="w-4 h-4" />
                                    Vencida
                                  </span>
                                  <span className="text-blue-400 text-[10px]">
                                    (Parcial)
                                  </span>
                                </span>
                              ) : pago.estado === 'parcial' ? (
                                <span className="flex items-center justify-center gap-1 text-blue-400 text-xs">
                                  <CreditCard className="w-4 h-4" />
                                  Parcial
                                </span>
                              ) : (
                                <span className={`flex items-center justify-center gap-1 text-xs ${estaVencida ? 'text-red-400' : 'text-amber-400'}`}>
                                  {estaVencida ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                                  {estaVencida ? 'Vencida' : 'Pendiente'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB: Cotizaciones */}
          {activeTab === 'cotizaciones' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  Cotizaciones Pendientes
                </h2>
                <button
                  onClick={() => navigate('/quotes/new', { state: { cliente } })}
                  className="text-sm text-[#D4AF37] hover:underline"
                >
                  + Nueva cotización
                </button>
              </div>
              
              {cotizaciones.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No hay cotizaciones pendientes</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cotizaciones.map(cot => (
                    <div
                      key={cot.id}
                      onClick={() => navigate(`/quotes/${cot.id}`)}
                      className="flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg cursor-pointer transition-colors"
                    >
                      <div>
                        <p className="font-medium">{cot.numero || 'Sin número'}</p>
                        <p className="text-xs text-zinc-400">{formatDate(cot.created_at)} • {cot.created_by_nombre}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{formatPrice(cot.total)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${ESTADO_COLORS[cot.estado] || ESTADO_COLORS.borrador}`}>
                          {cot.estado}
                        </span>
                        <ChevronRight className="w-4 h-4 text-zinc-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: Fichas */}
          {activeTab === 'fichas' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-blue-400" />
                  Fichas de Medidas
                </h2>
                <button
                  onClick={() => navigate('/sheets/new', { state: { cliente } })}
                  className="text-sm text-[#D4AF37] hover:underline"
                >
                  + Nueva ficha
                </button>
              </div>
              
              {fichas.length === 0 ? (
                <div className="text-center py-8">
                  <Ruler className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No hay fichas de medidas registradas</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fichas.map(ficha => (
                    <div
                      key={ficha.id}
                      onClick={() => navigate(`/sheets/${ficha.id}`)}
                      className="flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg cursor-pointer transition-colors"
                    >
                      <div>
                        <p className="font-medium">{ficha.nombre}</p>
                        <p className="text-xs text-zinc-400">
                          {ficha.fecha_evento ? `Evento: ${formatDate(ficha.fecha_evento)}` : formatDate(ficha.created_at)}
                          {ficha.created_by_nombre && ` • ${ficha.created_by_nombre}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${ESTADO_COLORS[ficha.estado] || ESTADO_COLORS.borrador}`}>
                          {ficha.estado}
                        </span>
                        <ChevronRight className="w-4 h-4 text-zinc-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: Puntos JPM */}
          {activeTab === 'puntos' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-400" />
                Puntos JPM
              </h2>
              
              {puntos.error ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                  <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-red-400 text-sm">Error al cargar puntos</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Saldo actual */}
                  <div className="bg-gradient-to-r from-purple-900/30 to-zinc-900 rounded-lg p-6 text-center">
                    <p className="text-zinc-400 text-sm mb-2">Saldo Disponible</p>
                    <p className="text-4xl font-bold text-purple-400">
                      {(puntos.saldo_puntos || 0).toLocaleString()} <span className="text-lg">pts</span>
                    </p>
                    <p className="text-emerald-400 text-sm mt-2">
                      = {formatPrice(puntos.valor_equivalente)} en beneficios
                    </p>
                    {puntos.valor_por_punto && (
                      <p className="text-zinc-500 text-xs mt-1">
                        (1 punto = {formatPrice(puntos.valor_por_punto)})
                      </p>
                    )}
                  </div>
                  
                  {/* Historial */}
                  <div>
                    <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Historial de Movimientos
                    </h3>
                    
                    {(!puntos.historial || puntos.historial.length === 0) ? (
                      <p className="text-zinc-500 text-center py-4 text-sm">
                        Sin movimientos registrados
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {puntos.historial.map((mov, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                mov.puntos > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                              }`}>
                                {mov.puntos > 0 ? (
                                  <TrendingUp className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Gift className="w-4 h-4 text-red-400" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium capitalize">
                                  {mov.tipo?.replace(/_/g, ' ')}
                                </p>
                                <p className="text-xs text-zinc-400">
                                  {formatDate(mov.created_at)}
                                  {mov.created_by_nombre && ` • ${mov.created_by_nombre}`}
                                </p>
                                {mov.descripcion && (
                                  <p className="text-xs text-zinc-500 mt-0.5">{mov.descripcion}</p>
                                )}
                              </div>
                            </div>
                            <span className={`font-bold ${mov.puntos > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {mov.puntos > 0 ? '+' : ''}{mov.puntos.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: Referidos */}
          {activeTab === 'referidos' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Referidos
              </h2>
              
              <div className="space-y-6">
                {/* Es aliado? */}
                {referidos.es_aliado && referidos.aliado_info && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-5 h-5 text-emerald-400" />
                      <span className="font-medium text-emerald-400">Este cliente es un Aliado</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-zinc-400">Código:</p>
                        <p className="font-mono text-[#D4AF37]">{referidos.aliado_info.codigo}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400">Tipo:</p>
                        <p className="capitalize">{referidos.aliado_info.tipo}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400">Comisiones totales:</p>
                        <p className="text-emerald-400">{formatPrice(referidos.aliado_info.comisiones_totales)}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400">Estado:</p>
                        <p className={referidos.aliado_info.activo ? 'text-green-400' : 'text-zinc-500'}>
                          {referidos.aliado_info.activo ? 'Activo' : 'Inactivo'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Referido por */}
                {referidos.referido_por && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm text-zinc-400 mb-2">Este cliente fue referido por:</p>
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:bg-blue-500/10 p-2 rounded-lg -mx-2 transition-colors"
                      onClick={() => navigate(`/clientes/${referidos.referido_por.id}`)}
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium">{referidos.referido_por.nombre}</p>
                        <p className="text-xs text-zinc-400">{referidos.referido_por.id_cliente}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-500 ml-auto" />
                    </div>
                  </div>
                )}
                
                {/* Clientes referidos */}
                <div>
                  <h3 className="text-sm font-medium text-zinc-400 mb-3">
                    Clientes Referidos por {cliente.nombre.split(' ')[0]}
                  </h3>
                  
                  {(!referidos.clientes_referidos || referidos.clientes_referidos.length === 0) ? (
                    <p className="text-zinc-500 text-center py-4 text-sm">
                      No ha referido a ningún cliente aún
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {referidos.clientes_referidos.map(ref => (
                        <div
                          key={ref.id}
                          onClick={() => navigate(`/clientes/${ref.id}`)}
                          className="flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-400" />
                            </div>
                            <div>
                              <p className="font-medium">{ref.nombre}</p>
                              <p className="text-xs text-zinc-400">{ref.id_cliente} • {formatDate(ref.created_at)}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-500" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Beneficios obtenidos */}
                {referidos.beneficios_obtenidos > 0 && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
                    <Gift className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-sm text-zinc-400">Puntos ganados por referidos:</p>
                    <p className="text-2xl font-bold text-purple-400">
                      +{referidos.beneficios_obtenidos.toLocaleString()} pts
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Registros sin vincular (para revisión) */}
          {registrosSinVincular && registrosSinVincular.cotizaciones_posibles?.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                Registros sin vincular (revisión manual)
              </h2>
              <p className="text-sm text-zinc-400 mb-4">
                Estas cotizaciones podrían pertenecer a este cliente pero no están vinculadas formalmente:
              </p>
              <div className="space-y-2">
                {registrosSinVincular.cotizaciones_posibles.map(cot => (
                  <div
                    key={cot.id}
                    onClick={() => navigate(`/quotes/${cot.id}`)}
                    className="flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg cursor-pointer transition-colors"
                  >
                    <div>
                      <p className="font-medium">{cot.numero}</p>
                      <p className="text-xs text-zinc-400">{cot.cliente_nombre} • {formatDate(cot.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{formatPrice(cot.total)}</span>
                      <Eye className="w-4 h-4 text-zinc-500" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Modal de eliminación de venta */}
      <EliminarVentaModal
        isOpen={!!ventaAEliminar}
        onClose={() => setVentaAEliminar(null)}
        venta={ventaAEliminar}
        clienteId={id}
        onSuccess={() => {
          // Refrescar los datos del cliente después de eliminar
          fetchCliente();
        }}
      />
    </div>
  );
};

export default ClienteDetalle;
